import React, { useState, useEffect } from 'react';
import { Box, OptimizationResult, PalletDimensions } from './types';
import { PalletOptimizer } from './utils/palletOptimizer';
import { PreOptimizationPage } from './components/PreOptimizationPage';
import { PostOptimizationPage } from './components/PostOptimizationPage';
import { VisualizationPage } from './components/VisualizationPage';
import { HomePage } from './components/HomePage';
import { Layout } from './components/Layout';
import { sampleBoxes } from './utils/sampleData';
import { testOptimizer } from './utils/testOptimizer';
import { StackingLoader } from './components/StackingLoader';
import { Meta } from './components/Meta';
import { Routes, Route } from 'react-router-dom';
import BlogIndex from './blog/BlogIndex';
import BlogPost1 from './blog/BlogPost1';
import BlogPost2 from './blog/BlogPost2';
import BlogPost3 from './blog/BlogPost3';
import BlogPost4 from './blog/BlogPost4';
import BlogPost5 from './blog/BlogPost5';
import BlogPost6 from './blog/BlogPost6';
import BlogPost7 from './blog/BlogPost7';
import BlogPost8 from './blog/BlogPost8';
import BlogPost9 from './blog/BlogPost9';
import BlogPost10 from './blog/BlogPost10';
import { Header } from './components/Header';
import { Analytics } from '@vercel/analytics/react';

type View = 'home' | 'optimization' | 'visualization';

// Wrapper components to set the initial view for /optimization and /visualization
function OptimizationRoute() {
  return <MainApp initialView="optimization" />;
}
function VisualizationRoute() {
  return <MainApp initialView="visualization" />;
}

function MainApp({ initialView }: { initialView?: View }) {
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [currentView, setCurrentView] = useState<View>(initialView || 'home');
  const [showLoader, setShowLoader] = useState(false);

  // Scroll to top when optimization finishes or visualization is shown
  useEffect(() => {
    if (result || currentView === 'visualization') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [result, currentView]);

  const handleAddBox = (box: Box) => {
    setBoxes([...boxes, box]);
  };

  const handleAddBoxes = (newBoxes: Box[]) => {
    setBoxes([...boxes, ...newBoxes]);
  };

  const handleRemoveBox = (index: number) => {
    setBoxes(boxes.filter((_, i) => i !== index));
  };

  const handleCalculate = (palletDimensions: PalletDimensions) => {
    setShowLoader(true);
    setTimeout(() => {
      const optimizer = new PalletOptimizer(palletDimensions);
    boxes.forEach(box => optimizer.addBox(box));
    const optimizationResult = optimizer.optimize();
    setResult(optimizationResult);
      setShowLoader(false);
    }, 4000);
  };

  const handleClear = () => {
    setBoxes([]);
    setResult(null);
  };

  const handleLoadSample = () => {
    setBoxes(sampleBoxes);
    setResult(null);
  };

  const handleTestOptimizer = () => {
    testOptimizer();
  };

  const handleNavigate = (view: View) => {
    setCurrentView(view);
  };

  const handleStartOptimization = () => {
    setCurrentView('optimization');
  };

  const handleNewOptimization = () => {
    setBoxes([]);
    setResult(null);
  };

  const handleViewVisualization = () => {
    setCurrentView('visualization');
  };

  // Show home page if current view is home
  if (currentView === 'home') {
    return (
      <>
        <Meta
          title="Pallet Builder – FCL Optimization Tool"
          description="Advanced FCL optimization tool for efficient pallet stacking and shipping logistics. Upload CSV files, track product IDs, and visualize your optimized pallets."
        />
        <Layout currentView={currentView} onNavigate={handleNavigate}>
          <HomePage onStartOptimization={handleStartOptimization} />
        </Layout>
      </>
    );
  }

  // Show visualization page if current view is visualization
  if (currentView === 'visualization') {
    return (
      <>
        <Meta
          title="Pallet Visualization – Pallet Builder"
          description="View your optimized pallet layouts and analyze stacking efficiency for FCL shipments."
        />
        <Layout 
          currentView={currentView} 
          onNavigate={handleNavigate}
        >
          <VisualizationPage result={result} />
        </Layout>
      </>
    );
  }

  // Show optimization page (either pre or post optimization)
  return (
    <>
      <Meta
        title="Optimize Your Pallets – Pallet Builder"
        description="Input box types, upload CSVs, and optimize your FCL pallet stacking for maximum efficiency."
      />
      <Layout currentView={currentView} onNavigate={handleNavigate}>
        {showLoader && <StackingLoader />}
        {result ? (
          <PostOptimizationPage 
            result={result}
            onNewOptimization={handleNewOptimization}
            onViewVisualization={handleViewVisualization}
          />
        ) : (
          <PreOptimizationPage 
            boxes={boxes}
            onAddBox={handleAddBox}
            onAddBoxes={handleAddBoxes}
            onRemoveBox={handleRemoveBox}
            onCalculate={handleCalculate}
            onClear={handleClear}
            onLoadSample={handleLoadSample}
          />
        )}
      </Layout>
    </>
  );
}

export default function App() {
  return (
    <>
      <Header />
      <Routes>
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/BlogPost1" element={<BlogPost1 />} />
        <Route path="/blog/BlogPost2" element={<BlogPost2 />} />
        <Route path="/blog/BlogPost3" element={<BlogPost3 />} />
        <Route path="/blog/BlogPost4" element={<BlogPost4 />} />
        <Route path="/blog/BlogPost5" element={<BlogPost5 />} />
        <Route path="/blog/BlogPost6" element={<BlogPost6 />} />
        <Route path="/blog/BlogPost7" element={<BlogPost7 />} />
        <Route path="/blog/BlogPost8" element={<BlogPost8 />} />
        <Route path="/blog/BlogPost9" element={<BlogPost9 />} />
        <Route path="/blog/BlogPost10" element={<BlogPost10 />} />
        <Route path="/optimization" element={<OptimizationRoute />} />
        <Route path="/visualization" element={<VisualizationRoute />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
      <Analytics />
    </>
  );
}
